// src/hooks/useDealerPlanPayment.js
// Generic Razorpay payment flow for listing credits & bidding passes

import { useState, useCallback } from 'react';

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

/**
 * @param {object} opts
 * @param {function} opts.onInitiate  - calls API to create razorpay order, returns { razorpay_order_id, amount_paisa, razorpay_key }
 * @param {function} opts.onConfirm   - calls API to confirm after payment, receives { razorpayOrderId, razorpayPaymentId, razorpaySignature }
 * @param {function} opts.onSuccess   - called after successful confirmation
 * @param {string}   opts.description - shown in Razorpay modal
 */
export default function useDealerPlanPayment({ onInitiate, onConfirm, onSuccess, description }) {
  const [step,       setStep]       = useState('idle'); // idle | processing | success | error
  const [errorMsg,   setErrorMsg]   = useState('');

  const pay = useCallback(async (phone = '') => {
    setStep('processing');
    setErrorMsg('');

    try {
      const sdkLoaded = await loadRazorpayScript();
      if (!sdkLoaded) throw new Error('Failed to load Razorpay SDK. Check your internet connection.');

      const initData = await onInitiate();
      const { razorpay_order_id, amount_paisa, razorpay_key } = initData;

      const options = {
        key:         razorpay_key,
        amount:      amount_paisa,
        currency:    'INR',
        name:        'CarYugo',
        description: description || 'Plan Purchase',
        order_id:    razorpay_order_id,
        prefill:     { contact: phone ? `91${phone}` : '' },
        theme:       { color: '#003566' },
        modal: {
          ondismiss: () => setStep('idle'),
        },
        handler: async (response) => {
          try {
            await onConfirm({
              razorpayOrderId:   response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });
            setStep('success');
            onSuccess?.();
          } catch (err) {
            setErrorMsg(err.response?.data?.error || 'Payment confirmation failed.');
            setStep('error');
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (res) => {
        setErrorMsg(res.error?.description || 'Payment failed.');
        setStep('error');
      });
      rzp.open();

    } catch (err) {
      setErrorMsg(err.response?.data?.error || err.message || 'Something went wrong.');
      setStep('error');
    }
  }, [onInitiate, onConfirm, onSuccess, description]);

  const reset = () => { setStep('idle'); setErrorMsg(''); };

  return { step, errorMsg, pay, reset };
}